import {
  Activity, Sparkles, Smile, Layers, AlignHorizontalDistributeCenter,
  Microscope, Stethoscope, Paintbrush, Anchor, Crown, Sun, Droplets,
  Scissors, HeartPulse, ShieldCheck, Bone, Bug,
} from "lucide-react";

export const CLINIC = {
  name: "Dr. Vinita's Kesari Dental Clinic",
  doctor: "Dr. Vinita Jain",
  credentials: "BDS, MDS",
  phone: "+91 9729002444",
  phoneDial: "+919729002444",
  whatsapp: "919729002444",
  mapsQuery: "DSS 379, Sector 16-17, Hisar, Haryana",
  // Google Business Profile share link (resolves to Knowledge Panel + Maps place;
  // GBP kgmid /g/11k9lgnfk9). Used for JSON-LD sameAs and user-facing CTAs.
  googleBusiness: "https://share.google/8SK9fT61KI0AEUlk2",
  // Dr. Vinita Jain's Instagram — used in Footer social row and JSON-LD Person.sameAs.
  instagram: "https://www.instagram.com/dr.vinitajain/",
  instagramHandle: "@dr.vinitajain",
};

export const IMAGES = {
  doctorHero: "https://customer-assets-eiarnc6j.emergentagent.net/job_vinita-implants/artifacts/ymwzpaql_WhatsApp%20Image%202026-07-22%20at%2010.45.29%20AM.jpeg",
  doctorSeated: "https://customer-assets-eiarnc6j.emergentagent.net/job_vinita-implants/artifacts/r2041idi_WhatsApp%20Image%202026-07-22%20at%2010.45.29%20AM%20%281%29.jpeg",
  clinicInterior: "https://customer-assets-eiarnc6j.emergentagent.net/job_vinita-implants/artifacts/wla15n2b_WhatsApp%20Image%202026-07-22%20at%2010.45.29%20AM%20%284%29.jpeg",
};

// Real before/after cases from Kesari Dental Clinic. Served from /public/gallery/,
// 1200x1200 JPEG, center-cropped square (aspect-square in BeforeAfter.jsx).
// Shown with patient consent. Order must match translations.js -> gallery.cases.
export const GALLERY_IMAGES = [
  { id: "fractured-tooth", before: "/gallery/case-1-before.jpg", after: "/gallery/case-1-after.jpg" },
  { id: "decayed-molar",   before: "/gallery/case-2-before.jpg", after: "/gallery/case-2-after.jpg" },
  { id: "scaling",         before: "/gallery/case-3-before.jpg", after: "/gallery/case-3-after.jpg" },
];

const GENERIC_MSG = {
  en: "Hello Dr. Vinita's Kesari Dental Clinic, I'd like to book a dental appointment. Please share the available slots.",
  hi: "नमस्ते डॉ. विनीता केसरी डेंटल क्लिनिक, मैं डेंटल अपॉइंटमेंट बुक करना चाहता/चाहती हूँ। कृपया उपलब्ध समय बताएं।",
};

export function waLink(message, lang = "en") {
  const text = message && message.trim().length ? message : GENERIC_MSG[lang] || GENERIC_MSG.en;
  return `https://wa.me/${CLINIC.whatsapp}?text=${encodeURIComponent(text)}`;
}

export function bookingMessage({ name, phone, service, datetime, message }, lang = "en") {
  const L = lang === "hi"
    ? {
        head: "नमस्ते डॉ. विनीता केसरी डेंटल क्लिनिक, मैं अपॉइंटमेंट बुक करना चाहता/चाहती हूँ।",
        name: "नाम", phone: "फ़ोन", service: "सेवा", dt: "पसंदीदा तारीख़/समय", msg: "संदेश",
      }
    : {
        head: "Hello Dr. Vinita's Kesari Dental Clinic, I'd like to book an appointment.",
        name: "Name", phone: "Phone", service: "Service", dt: "Preferred Date/Time", msg: "Message",
      };
  const text =
    `${L.head}\n` +
    `${L.name}: ${name || "-"}\n` +
    `${L.phone}: ${phone || "-"}\n` +
    `${L.service}: ${service || "-"}\n` +
    `${L.dt}: ${datetime || "-"}\n` +
    `${L.msg}: ${message || "-"}`;
  return `https://wa.me/${CLINIC.whatsapp}?text=${encodeURIComponent(text)}`;
}

export const callLink = `tel:${CLINIC.phoneDial}`;

// Structure only (icons + ids + grouping). Text lives in translations.
export const SERVICE_ICONS = {
  pyorrhoea: Activity, implants: Anchor, smile: Sparkles, veneers: Smile,
  aligners: AlignHorizontalDistributeCenter, microscopic: Microscope, rct: Stethoscope,
  fillings: Paintbrush, bridges: Layers, crowning: Crown, whitening: Sun,
  scaling: Droplets, extractions: Scissors,
  gums: HeartPulse, cyst: Bug, fracture: Bone,
};

export const SERVICE_GROUPS = [
  { key: "signature", ids: ["pyorrhoea", "implants", "smile", "veneers"] },
  { key: "general", ids: ["aligners", "microscopic", "rct", "fillings", "bridges", "crowning", "whitening", "scaling", "extractions"] },
  { key: "surgery", ids: ["gums", "cyst", "fracture"] },
];

// Order must mirror translations.js -> why.items:
// 1. Patient-Centered Care, 2. MDS Periodontist & Implantologist,
// 3. Gentle, Painless Treatment, 4. Sterile Hygiene Practices,
// 5. Microscopic Dentistry, 6. Compassionate 24×7 Care.
export const WHY_ICONS = [HeartPulse, Anchor, Smile, ShieldCheck, Microscope, Sparkles];

export const NAV_HREFS = ["#services", "#about", "#why-us", "#location", "#contact"];

// Testimonials kept verbatim (authentic patient wording). Not translated.
export const TESTIMONIALS = [
  { name: "Rohit Sharma", rating: 5, text: "I was really scared about my gum problem, bleeding ho raha tha since months. Dr. Vinita explained everything so patiently and the flap surgery was painless honestly. Best gum doctor in Hisar, highly recommend." },
  { name: "Priya Goyal", rating: 5, text: "Got my dental implant done here after my tooth broke. Was nervous about the cost and pain but the whole process was smooth. Clinic is very clean and hygienic. Dr. Vinita ma'am is very kind and never rushes you." },
  { name: "Sandeep Kumar", rating: 5, text: "Took my mother for pyorrhoea treatment. She's 68 and very anxious about dentists but doctor handled her so gently. The staff is polite too. Sector 16-17 mein iss se better dental clinic nahi milega." },
  { name: "Neha Bansal", rating: 5, text: "Did my smile designing and veneers before my sister's wedding. The result is amazing, everyone keeps asking about my teeth now 😄 Thank you doctor! Worth every rupee." },
  { name: "Ankit Verma", rating: 5, text: "Had a sudden tooth pain at night and they attended the emergency call without any issue. RCT was done properly in 2 sittings, no pain after that. Genuine clinic, doesn't do unnecessary treatments to loot money." },
];
